import React, {useState} from 'react';
import {DndContext, closestCenter, MouseSensor, TouchSensor, DragOverlay, useSensor, useSensors, MeasuringStrategy} from '@dnd-kit/core';
import {arrayMove, SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import * as css from '../Table.css';
import Row from '../newRow/dragRow';
import {useTranslation} from 'react-i18next';
import WarpTable from '../WrapTable';

function DragTable({
    list,
    onDragEnd = () => {},
    checked,
    buttonsChildren,
    title = '',
    scrollProps = {},
    searchProps,
    rowHeight = 70,
    header = false,
    customScrollbar = {}
}) {
    const [activeId, setActiveId] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const {t} = useTranslation('global');
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragMove = (event) => {
        const {over} = event;
        if(over) {
            const overIndex = list.findIndex((item) => item.key === over.id);
            setDragIndex(overIndex);
        }
    };

    const handleDragEnd = (event) => {
        const {active, over} = event;
        if(active.id !== over?.id) {
            const oldIndex = list.findIndex((item) => item.key === active.id);
            const newIndex = list.findIndex((item) => item.key === over.id);
            const newList = arrayMove(list, oldIndex, newIndex);
            onDragEnd(newList);
        }
        setActiveId(null);
        setDragIndex(null);
    };

    const handleDragCancel = () => {
        setActiveId(null);
        setDragIndex(null);
    };

    return (
        <WarpTable
            list={list}
            checked={checked}
            title={title}
            scrollProps={scrollProps}
            searchProps={searchProps}
            header={header}
            buttonsChildren={buttonsChildren}
            customScrollbar={customScrollbar}
        >
            {(getCheckedProps) => (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <SortableContext
                        items={list.map((item) => item.key)}
                        strategy={verticalListSortingStrategy}
                    >
                        <css.table>
                            {list.map((item, index) => (
                                <Row
                                    key={item.key}
                                    rowKey={item.key}
                                    index={index}
                                    rowHeight={rowHeight}
                                    checked={getCheckedProps(item.key)}
                                >
                                    <item.comp {...item.props} index={index} />
                                </Row>
                            ))}
                            {activeId !== null && (
                                <css.placeHolder
                                    rowHeight={rowHeight} 
                                    className="dragging" 
                                    style={{
                                        top: `${(dragIndex ?? 0) * (rowHeight + 8) + 8}px`
                                    }}
                                >
                                    {t('drag')}
                                </css.placeHolder>
                            )}
                        </css.table>
                    </SortableContext>
                    <DragOverlay adjustScale style={{transformOrigin: '0 0'}}>
                        {activeId !== null ? (
                            <Row
                                rowKey={activeId}
                                rowHeight={rowHeight}
                                isFloatingDragging
                            >
                                {(() => {
                                    const activeItem = list.find((item) => item.key === activeId);
                                    if(activeItem?.comp) {
                                        const ActiveComponent = activeItem.comp;
                                        return <ActiveComponent {...activeItem.props} />;
                                    }
                                    return null;
                                })()}
                            </Row>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}
        </WarpTable>
    );
}

export default DragTable;
